import { useEffect, useState } from "react";
import SubscribeButton from "./subscribe-button/SubscribeButton";
import { SubscribeType } from "./types";
import { baseUrl, getDataFromKms, translate } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";

export interface SubscribeProps extends SubscribeType {
    linksCount?: number;
    isBorderless?: boolean;
    onSubscribe?: (isSubscribed: boolean) => void;
}

/**
 * server response for calls to subscribe / unsubscribe
 */
type SubscriptionResponse = {
    // true if the action succeeded, false otherwise
    success: boolean;

    // true means this is subscription attempt, false means un-subscription attempt.
    // it does not refer to the current user's subscription status.
    isSubscribed: boolean;

    // optional translated error. not all flows return an error message on failure.
    error?: string;
};

/**
 *  channel subscribe
 */
export const Subscribe = (props: SubscribeProps) => {
    const { linksCount = 0, subscribed, channelId, subscribeUrl, unsubscribeUrl, isBorderless, onSubscribe } = props;

    const [isSubscribed, setIsSubscribed] = useState<boolean>(subscribed);
    const [processing, setProcessing] = useState<boolean>(false);
    const { showToast } = useToastsContext();

    const handleSuccess = (data: SubscriptionResponse) => {
        if (!data.success) {
            handleError(data.error);
            return;
        }
        setProcessing(false);
        setIsSubscribed(data.isSubscribed);

        showToast({
            severity: "success",
            message: data.isSubscribed
                ? translate("You have subscribed to the channel.")
                : translate("You have been unsubscribed from channel."),
        });

        onSubscribe?.(data.isSubscribed);
    };

    const handleError = (msg?: string) => {
        setProcessing(false);
        showToast({
            severity: "error",
            message: msg?.trim()
                ? <span>{msg.trim()}</span>
                : !isSubscribed
                    ? translate("Error subscribing to the channel.")
                    : translate("Error unsubscribing from channel."),
        });
    };

    const onToggle = () => {
        setProcessing(true);
        getDataFromKms(
            `${baseUrl}${isSubscribed ? unsubscribeUrl : subscribeUrl}`,
            { channelId },
            handleSuccess,
            false,
            handleError
        );
    };

    useEffect(() => {
        const searchValue = subscribed ? "subscribe" : "unsubscribe";
        window.history.replaceState(null, "", window.location.href.replace(`/${searchValue}`, ""));
    }, []);

    const buttonColor = isBorderless ? "secondary" : linksCount === 0 ? "primary" : "translucent";
    const useIconButton = linksCount > 1;

    return (
        <>
            <SubscribeButton
                subscribed={isSubscribed}
                onToggle={onToggle}
                processing={processing}
                buttonColor={buttonColor}
                useIconButton={useIconButton}
                isBorderless={isBorderless}
            />
        </>
    );
};
